import _ from 'lodash';
import {
  MODELS,
  CONFIG,
  TRIMS,
  EXTERIOR,
  WHEELS,
  INTERIOR,
  PACKAGES,
  OPTIONS,
  SUMMARY,
} from '../../common/steps';
import MediaQuery from '../../utils/MediaQuery';
import { startsWith, includes, endsWith } from 'lodash';

const getOrientation = () =>
  window.innerWidth >= window.innerHeight ? 'Landscape' : 'Portrait';

const getViewport = () => `${window.innerWidth}x${window.innerHeight}`;

const getRenderedExperience = () => MediaQuery.current;

const getBrandSymbol = (brand) => {
  switch (_.toLower(brand)) {
    case 'gmc-fleet':
    case 'chevrolet-fleet':
    case 'cadillac-fleet':
    case 'buick-fleet':
      return 'fleet';
    case 'buick':
    case 'gmc':
      return brand;
    case 'chevrolet':
      return 'ch';
    case 'cadillac':
      return 'ca';
    default:
      return '';
  }
};

const getSiteName = (country) => {
  const siteName =
    window.appResources.worldPlatformMode === 'QUANTUM' ? 'quantum_' : 'gmwp_';
  return siteName + country;
};

const regionFullName = {
  us: 'north america',
  ca: 'north america',
  br: 'south america',
};

export const initialize = (postalCode) => {
  const {
    language,
    year,
    brand,
    carline,
    continent,
    featureGroup,
    bodyStyle,
  } = window.appResources;
  window.appResources;

  const region = _.toLower(continent);
  const country = _.toLower(window.appResources.country);
  const siteSectionsLevel5 = `vehicles:${year}:${featureGroup}:${carline}:build-your-own:trims`;

  window.digitalData = {
    pageInfo: {
      pageName: `${getBrandSymbol(
        brand
      )}:${region}:${country}:${language}:t1:${siteSectionsLevel5}`,
      siteSectionsLevel1: 'vehicles',
      siteSectionsLevel2: year,
      siteSectionsLevel3: featureGroup,
      siteSectionsLevel4: carline,
      siteSectionsLevel5: siteSectionsLevel5,
      universalID: 'AB52350',
      siteName: getSiteName(country),
      url: window.location.href,
      languageSelected: language,
      brand,
      country,
      region: regionFullName[country],
      renderedExperience: getRenderedExperience(),
      viewport: getViewport(),
      orientation: getOrientation(),
      seoStrategyPageName: document.title,
      pageType: 'tool',
      pageSubType: '',
    },
    vehicleInfo: {
      modelName: carline,
      modelYear: year,
      vehicleType: featureGroup,
      bodyStyle,
      trimLevel: '',
    },
    visitorProfile: {
      zipCode: postalCode,
    },
    toolsInfo: {
      configuratorSections: '',
      dealerId: '',
    },
    forms: {},
  };

  window._satellite && window._satellite.track('byo-start-event');

  window.addEventListener('resize', () => {
    window.digitalData.pageInfo.viewport = getViewport();
    window.digitalData.pageInfo.orientation = getOrientation();
    window.digitalData.pageInfo.renderedExperience = getRenderedExperience();
  });
};

const updateSiteSectionsLevel5 = (pageNameSuffix) => {
  if (pageNameSuffix) {
    const {
      siteSectionsLevel1,
      siteSectionsLevel2,
      siteSectionsLevel3,
      siteSectionsLevel4,
    } = window.digitalData.pageInfo;

    window.digitalData.pageInfo.siteSectionsLevel5 = `${siteSectionsLevel1}:${siteSectionsLevel2}:${siteSectionsLevel3}:${siteSectionsLevel4}:build-your-own:${pageNameSuffix}`;
    const pageNamePrefix = window.digitalData.pageInfo.pageName
      .split(':')
      .slice(0, 5)
      .join(':');
    window.digitalData.pageInfo.pageName = `${pageNamePrefix}:${window.digitalData.pageInfo.siteSectionsLevel5}`;
  }
};

export const trackStateChanged = (toPath, ignoreTracking) => {
  let trackedEvent = '';
  let pageNameSuffix = toPath;
  const step = toPath.split('/')[0];
  window.digitalData.pageInfo.url = window.location.href;

  switch (toPath) {
    case 'packagesandoptions':
      trackedEvent = 'byo-step-load';
      pageNameSuffix = 'options';
      window.digitalData.toolsInfo.configuratorSections = toPath;
      break;
    case 'confirmation':
      trackedEvent = 'byo-confirmation-load';
      window.digitalData.toolsInfo.configuratorSections = toPath;
      break;
    case CONFIG:
    case TRIMS:
    case EXTERIOR:
    case WHEELS:
    case INTERIOR:
    case PACKAGES:
    case OPTIONS:
    case MODELS:
      trackedEvent = 'byo-step-load';
      window.digitalData.toolsInfo.configuratorSections = toPath;
      break;
    case SUMMARY:
      trackedEvent = 'byo-summary-load';
      window.digitalData.toolsInfo.configuratorSections = toPath;
      break;
    default:
      trackedEvent = 'byo-modal-load';

      if (startsWith(toPath, 'trims/features')) {
        pageNameSuffix = `${step}:trims details modal`;
      } else if (toPath === 'trims/compare') {
        pageNameSuffix = `${step}:trims compare modal`;
      } else if (endsWith(toPath, 'trims/driveTypes')) {
        pageNameSuffix = `${step}:trims modal`;
      } else if (
        startsWith(toPath, 'packages/package') ||
        startsWith(toPath, 'summary/packages/option')
      ) {
        pageNameSuffix = `${step}:package details modal`;
      } else if (endsWith(toPath, 'savedbuilds')) {
        pageNameSuffix = `${step}:saved builds modal`;
      } else if (endsWith(toPath, 'moreviews')) {
        pageNameSuffix = `${step}:more-views`;
      } else if (startsWith(toPath, 'exterior/option')) {
        pageNameSuffix = `${step}:exterior details modal`;
      } else if (startsWith(toPath, 'interior/option')) {
        pageNameSuffix = `${step}:interior details modal`;
      } else if (startsWith(toPath, 'wheels/option')) {
        pageNameSuffix = `${step}:wheels details modal`;
      } else if (endsWith(toPath, 'packagesandoptions')) {
        pageNameSuffix = 'options';
      } else if (includes(toPath, 'option')) {
        pageNameSuffix = `${step}:option details modal`;
      } else if (startsWith(toPath, 'summary/share')) {
        pageNameSuffix = `${step}:send link modal`;
      } else if (startsWith(toPath, 'config/specifications')) {
        pageNameSuffix = `${step}:body details modal`;
      } else if (startsWith(toPath, 'summary/payment')) {
        pageNameSuffix = 'payment estimator';
      } else if (startsWith(toPath, 'config/compare')) {
        pageNameSuffix = `${step}:body compare modal`;
      }
      break;
  }

  if (!window.digitalData.pageInfo.renderedExperience) {
    window.digitalData.pageInfo.renderedExperience = getRenderedExperience();
  }

  updateSiteSectionsLevel5(pageNameSuffix);
  if (window._satellite && !ignoreTracking) {
    window._satellite.track(trackedEvent);
  }
};

const getZeroLight = (params, config) => {
  const { peg, selections } = config;
  const { modelyear } = params;
  const mmcPeg = peg.replace(/__/, '_');
  const { exteriorColor, interiorColor, rpos } = selections;

  const rposString = rpos
    .filter((rpo) => rpo !== exteriorColor && rpo !== interiorColor)
    .join('|');

  return `${modelyear}|${mmcPeg}|${exteriorColor}|${interiorColor}|${rposString}`;
};

export const setConfig = (params, fullConfig) => {
  let vehicleInfo = window.digitalData.vehicleInfo;
  let toolsInfo = window.digitalData.toolsInfo;

  window.digitalData.visitorProfile.zipCode = params.postalCode;

  vehicleInfo.bodyStyle = params.bodystyle;
  vehicleInfo.trimLevel = _.get(fullConfig, 'selections.trimName');

  toolsInfo.engine = _.get(fullConfig, 'selections.engine');
  toolsInfo.transmission = _.get(fullConfig, 'selections.transmission');
  toolsInfo.wheels = _.flatMap(
    _.get(fullConfig, 'wheels.categories', []),
    'options'
  )
    .filter((x) => x.selected === true)
    .map((x) => x.code)
    .join();
  toolsInfo.options = _.get(fullConfig, 'selections.rpos').join();
  toolsInfo.accessories = '';
  toolsInfo.configIntColorUpholstery = _.get(
    fullConfig,
    'selections.interiorColor'
  );
  toolsInfo.configExtColor = _.get(fullConfig, 'selections.exteriorColor');
  toolsInfo.configuratorStartPrice = _.get(
    fullConfig,
    'summary.prices.standardVehiclePrice.text'
  );

  toolsInfo.configModelPackageVersion = _.flatMap(
    _.get(fullConfig, 'config.packages', []),
    'options'
  )
    .filter((x) => x.selected === true)
    .map((x) => x.code)
    .join();

  toolsInfo.configuratorEndPrice =
    _.get(fullConfig, 'summary.prices.netPrice.text') ||
    'See dealer for pricing';

  window.digitalData.forms.zeroLight = getZeroLight(params, fullConfig);
};

export const trackTargetUpdated = () =>
  window._satellite && window._satellite.track('byo-target-update');

export const trackModalLoaded = (modalName) => {
  updateSiteSectionsLevel5(modalName);
  window._satellite && window._satellite.track('byo-modal-load');
};

export const trackModalClosed = (currentPath, basename) => {
  const currentToPath = currentPath.replace(`${basename}/`, '');
  trackStateChanged(currentToPath, true);
};
